import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, EngineKeyline, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { listProviders } from '@/data/sources-social';
import { channelLabel } from '@/data/studio-layouts';
import type { Provider } from '@/types/social';
import { assignConnectionDomain, listSocialConnections } from '@/data/sources-connections';
import {
  assignmentView,
  connectionDomainLabel,
  groupByProvider,
  isSocialDisabled,
  listConnectionsErrorMessage,
  socialConnectionRow,
  UNASSIGNED_DOMAIN_VALUE,
  assignDomainErrorMessage,
  PENDING_ASSIGNMENT_HINT,
  type DomainChoiceOption,
  type SocialConnectionRow,
} from '@/data/social-connections-model';

/* ----------------------------------------------------------------------------
 * Social › Connections — the account↔domain assignment surface (Lane B).
 *
 * BookedCentral is multi-domain, so a connected Facebook/Instagram account must
 * declare WHICH domain it publishes for. This screen's PRIMARY job is that
 * assignment: it lists the workspace's connected social accounts (GET
 * /v1/social/connections), grouped by provider, each with a per-account Domain
 * dropdown (POST .../assign-domain) reusing the SAME domain list that drives the
 * shell's "Viewing" selector (useActiveDomain).
 *
 * HONESTY: the list never fabricates an account; project_id null reads as
 * "Unassigned"; the 503 social-disabled and 401 states render plainly; and a
 * persisted:false assign (an honest pre-migration no-op) keeps the account
 * Unassigned with a subtle "takes effect after the pending update" hint — never
 * a false success. The provider directory (with its owner-gated, disabled
 * Connect) is kept below as secondary context.
 * -------------------------------------------------------------------------- */

const STATUS_TONE = { success: 'success', neutral: 'neutral', warning: 'warning' } as const;

export function ConnectionsPage() {
  const { activeWorkspace } = useAuth();
  const qc = useQueryClient();
  const wsId = activeWorkspace?.id ?? 'none';

  const { domains } = useActiveDomain();
  const domainChoices: DomainChoiceOption[] = useMemo(
    () => domains.map((d) => ({ projectId: d.projectId, hostname: d.hostname, displayName: d.displayName })),
    [domains],
  );

  const connections = useQuery({
    queryKey: ['social', 'connections', wsId],
    queryFn: ({ signal }) => listSocialConnections(signal),
  });

  const onChanged = () => qc.invalidateQueries({ queryKey: ['social', 'connections', wsId] });

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Connections"
        subtitle="Assign each connected social account to the domain it publishes for. Accounts left unassigned aren’t used for any domain’s posts."
        actions={<Badge tone="ai">Social</Badge>}
      />
      <EngineKeyline className="mb-6" />

      <ConnectedAccounts
        state={connections}
        domains={domainChoices}
        onChanged={onChanged}
      />

      <ProviderDirectory />
    </div>
  );
}

// ---- Connected accounts (the primary assignment surface) --------------------

function ConnectedAccounts({
  state,
  domains,
  onChanged,
}: {
  state: ReturnType<typeof useQuery<Awaited<ReturnType<typeof listSocialConnections>>, unknown>>;
  domains: DomainChoiceOption[];
  onChanged: () => void;
}) {
  const { data, isLoading, isError, error, refetch } = state;

  const groups = useMemo(
    () => groupByProvider((data ?? []).map(socialConnectionRow)),
    [data],
  );

  return (
    <Card className="mb-6 p-5">
      <h3 className="mb-1 text-sm font-medium text-text-primary">Connected accounts</h3>
      <p className="mb-4 text-xs text-text-tertiary">
        Each account belongs to one domain (or none). Pick a domain to route that account’s posts.
      </p>

      {isLoading && <LoadingState rows={3} />}

      {/* The connector-disabled (503) state is a calm "not enabled yet" panel,
          not a red error — it is an expected owner-gated state, not a fault. */}
      {!isLoading && isError && isSocialDisabled(error) && (
        <EmptyState
          title="Social connections aren’t enabled yet"
          teach={listConnectionsErrorMessage(error)}
        />
      )}

      {!isLoading && isError && !isSocialDisabled(error) && (
        <ErrorState error={error} onRetry={() => refetch()} />
      )}

      {!isLoading && !isError && (!data || data.length === 0) && (
        <EmptyState
          title="No social accounts connected yet"
          teach="Once a Facebook or Instagram account is connected, it appears here so you can assign it to a domain."
        />
      )}

      {!isLoading && !isError && data && data.length > 0 && (
        <div className="space-y-6">
          {groups.map((group) => (
            <section key={group.provider}>
              <div className="mb-2 flex items-center gap-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
                  {group.providerLabel}
                </h4>
                <span className="text-xs text-text-tertiary">
                  {group.rows.length} account{group.rows.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="space-y-2">
                {group.rows.map((row) => (
                  <AccountRow key={row.id} row={row} domains={domains} onChanged={onChanged} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </Card>
  );
}

function AccountRow({
  row,
  domains,
  onChanged,
}: {
  row: SocialConnectionRow;
  domains: DomainChoiceOption[];
  onChanged: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border-subtle bg-inset/30 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-text-primary">{row.label}</span>
          <Badge tone={STATUS_TONE[row.statusTone]}>{row.statusLabel}</Badge>
        </div>
        <p className="mt-0.5 truncate text-xs text-text-tertiary">
          {row.accountRef ? `${row.accountRef} · ` : ''}Connected {row.connectedLabel}
        </p>
      </div>
      <DomainAssign
        connectionId={row.id}
        projectId={row.projectId}
        domains={domains}
        onChanged={onChanged}
      />
    </div>
  );
}

// ---- Per-account domain association (dropdown + honest persisted handling) --

function DomainAssign({
  connectionId,
  projectId,
  domains,
  onChanged,
}: {
  connectionId: number;
  projectId: number | null;
  domains: DomainChoiceOption[];
  onChanged: () => void;
}) {
  // The value shown by the picker. Server value is authoritative; we only ever
  // reflect a change once the server confirms it PERSISTED (honest no-op aware).
  const [value, setValue] = useState<number | null>(projectId);
  const [pendingHint, setPendingHint] = useState(false);
  useEffect(() => {
    setValue(projectId);
    setPendingHint(false);
  }, [projectId]);

  const assign = useMutation({
    mutationFn: (next: number | null) => assignConnectionDomain(connectionId, next),
    onMutate: () => setPendingHint(false),
    onSuccess: (res) => {
      const view = assignmentView(projectId, res);
      // persisted:false -> keep the account on its previous (Unassigned) domain
      // and show the pending hint. Never render the no-op as a success.
      setValue(view.effectiveProjectId);
      setPendingHint(view.pending);
      if (view.persisted) onChanged();
    },
    onError: () => {
      setValue(projectId);
      setPendingHint(false);
    },
  });

  const noDomains = domains.length === 0;

  return (
    <div className="flex flex-col gap-1 sm:items-end">
      <label className="flex items-center gap-2 text-xs text-text-tertiary">
        <span className="sm:sr-only">Domain</span>
        <select
          aria-label={`Assign ${connectionId} to a domain`}
          value={value == null ? UNASSIGNED_DOMAIN_VALUE : String(value)}
          disabled={assign.isPending || noDomains}
          onChange={(e) => {
            const raw = e.target.value;
            const next = raw === UNASSIGNED_DOMAIN_VALUE ? null : Number(raw);
            assign.mutate(next);
          }}
          className="rounded-md border border-border-default bg-canvas px-2 py-1 text-xs text-text-primary disabled:opacity-60"
        >
          <option value={UNASSIGNED_DOMAIN_VALUE}>Unassigned</option>
          {domains.map((d) => (
            <option key={d.projectId} value={String(d.projectId)}>
              {connectionDomainLabel(d.projectId, domains)}
            </option>
          ))}
        </select>
      </label>

      {/* Settled reflection of the CURRENT assignment (server-confirmed only). */}
      <span className="text-[11px] text-text-tertiary">
        {value == null ? 'Unassigned' : connectionDomainLabel(value, domains)}
      </span>

      {noDomains && (
        <span className="text-[11px] text-text-tertiary">Connect a domain first to assign accounts.</span>
      )}
      {assign.isPending && <span className="text-[11px] text-text-tertiary">Saving…</span>}
      {pendingHint && <span className="text-[11px] text-warning">{PENDING_ASSIGNMENT_HINT}</span>}
      {assign.isError && <span className="text-[11px] text-danger">{assignDomainErrorMessage(assign.error)}</span>}
    </div>
  );
}

// ---- Provider directory (secondary — owner-gated, Connect disabled) ---------

const DIR_STATE_TONE: Record<Provider['connectionState'], 'neutral' | 'success' | 'warning' | 'danger'> = {
  not_connected: 'warning', pending: 'neutral', connected: 'success', error: 'danger',
};
const DIR_STATE_LABEL: Record<Provider['connectionState'], string> = {
  not_connected: 'Connection required', pending: 'Pending', connected: 'Connected', error: 'Error',
};

function ProviderDirectory() {
  const q = useQuery({ queryKey: ['social-providers'], queryFn: listProviders });
  return (
    <Card className="p-5">
      <h3 className="mb-1 text-sm font-medium text-text-primary">Add a provider</h3>
      <p className="mb-4 text-xs text-text-tertiary">
        Connecting a new provider needs owner-provisioned credentials. Until then, Connect stays disabled by design.
      </p>
      {q.isLoading ? (
        <LoadingState rows={2} />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {q.data?.providers.map((p) => (
            <div key={p.provider} className="flex items-center justify-between rounded-md border border-border-subtle p-4">
              <div>
                <p className="text-sm font-medium text-text-primary">{channelLabel(p.provider)}</p>
                <p className="text-xs text-text-tertiary">
                  Up to {p.channelSpec.maxChars.toLocaleString('en-GB')} chars · {p.channelSpec.maxMedia} media
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge tone={DIR_STATE_TONE[p.connectionState]}>{DIR_STATE_LABEL[p.connectionState]}</Badge>
                <Button variant="secondary" disabled title="Owner action pending (G-SOC-3)">Connect</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
